import React, { useState, useEffect } from 'react';
import { adminFetch } from '../../api/client';
import cronstrue from 'cronstrue';

const API_URL = process.env.REACT_APP_API_URL || '';

// ============================================================
// CronBuilder sub-component
// ============================================================

const CronBuilder = ({ value, onChange }) => {
  const [mode, setMode] = useState('custom');
  const [hour, setHour] = useState('10');
  const [minute, setMinute] = useState('00');
  const [day, setDay] = useState('1');

  // Parse initial value once on mount
  useEffect(() => {
    if (!value) return;
    const parts = value.split(' ');
    if (parts.length < 5) {
      setMode('custom');
      return;
    }
    // Daily: M H * * *
    if (parts[2] === '*' && parts[3] === '*' && parts[4] === '*') {
      setMode('daily');
      setMinute(parts[0].padStart(2, '0'));
      setHour(parts[1].padStart(2, '0'));
    // Weekly: M H * * D
    } else if (parts[2] === '*' && parts[3] === '*' && parts[4] !== '*') {
      setMode('weekly');
      setMinute(parts[0].padStart(2, '0'));
      setHour(parts[1].padStart(2, '0'));
      setDay(parts[4]);
    } else {
      setMode('custom');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally runs once — avoids sync loops with parent value prop

  const updateCron = (newMode, h, m, d) => {
    let cron = value;
    if (newMode === 'daily') {
      cron = `${parseInt(m)} ${parseInt(h)} * * *`;
    } else if (newMode === 'weekly') {
      cron = `${parseInt(m)} ${parseInt(h)} * * ${d}`;
    }
    onChange(cron);
  };

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    setMode(newMode);
    if (newMode !== 'custom') {
      updateCron(newMode, hour, minute, day);
    }
  };

  const handleTimeChange = (type, val) => {
    if (type === 'hour') {
      setHour(val);
      updateCron(mode, val, minute, day);
    } else {
      setMinute(val);
      updateCron(mode, hour, val, day);
    }
  };

  const handleDayChange = (val) => {
    setDay(val);
    updateCron(mode, hour, minute, val);
  };

  let description = '';
  try {
    description = cronstrue.toString(value);
  } catch {
    description = 'Invalid cron expression';
  }

  return (
    <div
      className="admin2-cron-builder"
      style={{
        marginTop: '10px',
        padding: '10px',
        background: '#f8fafc',
        borderRadius: '4px',
        border: '1px solid #e2e8f0',
      }}
    >
      <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#4b5563' }}>
        {description}
        <span style={{ marginLeft: '8px', fontSize: '0.85em', color: '#6b7280', fontWeight: 'normal' }}>
          (UTC)
        </span>
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={mode} onChange={handleModeChange} style={{ padding: '5px' }}>
          <option value="custom">Custom (Advanced)</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>

        {mode !== 'custom' && (
          <>
            <span>at</span>
            <select
              value={hour}
              onChange={(e) => handleTimeChange('hour', e.target.value)}
              style={{ padding: '5px' }}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i.toString().padStart(2, '0')}>
                  {i.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
            <span>:</span>
            <select
              value={minute}
              onChange={(e) => handleTimeChange('minute', e.target.value)}
              style={{ padding: '5px' }}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i * 5} value={(i * 5).toString().padStart(2, '0')}>
                  {(i * 5).toString().padStart(2, '0')}
                </option>
              ))}
            </select>
          </>
        )}

        {mode === 'weekly' && (
          <>
            <span>on</span>
            <select value={day} onChange={(e) => handleDayChange(e.target.value)} style={{ padding: '5px' }}>
              <option value="1">Monday</option>
              <option value="2">Tuesday</option>
              <option value="3">Wednesday</option>
              <option value="4">Thursday</option>
              <option value="5">Friday</option>
              <option value="6">Saturday</option>
              <option value="0">Sunday</option>
            </select>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================
// CronJobsTab
// Usage: <CronJobsTab user={user} />
// ============================================================

const CronJobsTab = ({ user }) => {
  const [cronJobs, setCronJobs] = useState([]);
  const [availableScripts, setAvailableScripts] = useState([]);
  const [showJobForm, setShowJobForm] = useState(false);
  const [isEditingJob, setIsEditingJob] = useState(false);
  const [jobForm, setJobForm] = useState({ name: '', script: '', cron: '', enabled: true });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // ---- helpers ----

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const apiFetch = async (endpoint, options = {}) => {
    setLoading(true);
    try {
      // Identity travels as the bearer token added by adminFetch.
      const headers = { 'Content-Type': 'application/json' };
      const res = await adminFetch(
        `${API_URL}${endpoint}`,
        { ...options, headers: { ...headers, ...options.headers } }
      );
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        await res.text();
        throw new Error(`Server response was not JSON. Status: ${res.status}`);
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      return data;
    } catch (e) {
      showToast(`Error: ${e.message}`, 'error');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ---- data fetching ----

  const fetchScheduler = async () => {
    const data = await apiFetch('/api/admin/scheduler');
    if (data) setCronJobs(data.jobs);
  };

  const fetchScripts = async () => {
    const data = await apiFetch('/api/admin/scripts');
    if (data) setAvailableScripts(data.scripts);
  };

  useEffect(() => {
    fetchScheduler();
    fetchScripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- job actions ----

  const handleSaveJob = async () => {
    const action = isEditingJob ? 'update' : 'add';
    const data = await apiFetch('/api/admin/scheduler', {
      method: 'POST',
      body: JSON.stringify({ action, job: jobForm }),
    });
    if (data) {
      setCronJobs(data.jobs);
      setShowJobForm(false);
      setIsEditingJob(false);
      setJobForm({ name: '', script: '', cron: '', enabled: true });
      showToast(`Job "${jobForm.name}" ${action === 'add' ? 'added' : 'updated'}.`, 'success');
    }
  };

  const handleDeleteJob = async (name) => {
    if (!window.confirm(`Delete job "${name}"?`)) return;
    const data = await apiFetch('/api/admin/scheduler', {
      method: 'POST',
      body: JSON.stringify({ action: 'delete', job: { name } }),
    });
    if (data) {
      setCronJobs(data.jobs);
      showToast(`Job "${name}" deleted.`, 'info');
    }
  };

  const handleRunJob = async (name) => {
    const data = await apiFetch('/api/admin/scheduler/run', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    if (data) {
      showToast(`Job "${name}" triggered. Check logs.`, 'success');
    }
  };

  const handleToggleJob = async (job) => {
    const updated = { ...job, enabled: !job.enabled };
    const data = await apiFetch('/api/admin/scheduler', {
      method: 'POST',
      body: JSON.stringify({ action: 'update', job: updated }),
    });
    if (data) setCronJobs(data.jobs);
  };

  const openAddForm = () => {
    setIsEditingJob(false);
    setJobForm({ name: '', script: availableScripts[0] || '', cron: '* * * * *', enabled: true });
    setShowJobForm(true);
  };

  const openEditForm = (job) => {
    setIsEditingJob(true);
    setJobForm(job);
    setShowJobForm(true);
  };

  const cancelForm = () => {
    setShowJobForm(false);
    setJobForm({ name: '', script: '', cron: '', enabled: true });
  };

  // ---- render ----

  return (
    <div className="admin2-cron-tab">
      {/* UTC notice */}
      <div className="admin2-info-banner">
        <span style={{ fontSize: '1.2em' }}>ℹ️</span>
        <span>All cron job times are in <strong>UTC</strong> timezone (server time).</span>
      </div>

      {/* Header */}
      <div className="admin2-section-header">
        <h3 className="admin2-section-title">Scheduled Jobs</h3>
        <button className="admin2-btn-primary" onClick={openAddForm} disabled={loading}>
          + Add Job
        </button>
      </div>

      {/* Job Form */}
      {showJobForm && (
        <div className="admin2-job-form">
          <input
            className="admin2-input"
            placeholder="Job Name"
            value={jobForm.name}
            onChange={(e) => setJobForm({ ...jobForm, name: e.target.value })}
            disabled={isEditingJob}
          />
          <select
            className="admin2-select"
            value={jobForm.script}
            onChange={(e) => setJobForm({ ...jobForm, script: e.target.value })}
          >
            <option value="">Select Script</option>
            {availableScripts.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input
            className="admin2-input"
            placeholder="Cron Expression (e.g. 0 10 * * 1)"
            value={jobForm.cron}
            onChange={(e) => setJobForm({ ...jobForm, cron: e.target.value })}
          />
          <CronBuilder
            value={jobForm.cron}
            onChange={(newCron) => setJobForm({ ...jobForm, cron: newCron })}
          />
          <label className="admin2-checkbox-label">
            <input
              type="checkbox"
              checked={jobForm.enabled}
              onChange={(e) => setJobForm({ ...jobForm, enabled: e.target.checked })}
            />
            Enabled
          </label>
          <div className="admin2-form-actions">
            <button className="admin2-btn-primary" onClick={handleSaveJob} disabled={loading}>
              Save
            </button>
            <button className="admin2-btn-secondary" onClick={cancelForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Jobs Table */}
      {loading && !cronJobs.length ? (
        <div className="admin2-loading">Loading...</div>
      ) : (
        <table className="admin2-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Script</th>
              <th>Schedule</th>
              <th>Status</th>
              <th>Last Run</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {cronJobs.length === 0 ? (
              <tr>
                <td colSpan={6} className="admin2-table-empty">No jobs configured.</td>
              </tr>
            ) : (
              cronJobs.map((job) => {
                let humanSchedule = job.cron;
                try { humanSchedule = cronstrue.toString(job.cron); } catch { /* keep raw */ }

                return (
                  <tr key={job.name} className={!job.enabled ? 'admin2-row-disabled' : ''}>
                    <td>{job.name}</td>
                    <td>{job.script}</td>
                    <td>
                      <code className="admin2-code">{job.cron}</code>
                      <div className="admin2-cron-human">{humanSchedule}</div>
                    </td>
                    <td>
                      <span className={`admin2-badge ${job.enabled ? 'admin2-badge-success' : 'admin2-badge-neutral'}`}>
                        {job.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>
                      {job.lastRun ? (
                        <>
                          <div>{new Date(job.lastRun).toLocaleString()}</div>
                          <span className={`admin2-badge ${job.lastStatus === 'Success' ? 'admin2-badge-success' : 'admin2-badge-error'}`}>
                            {job.lastStatus}
                          </span>
                        </>
                      ) : (
                        <span className="admin2-muted">Never</span>
                      )}
                    </td>
                    <td className="admin2-table-actions">
                      <button className="admin2-btn-sm" onClick={() => handleToggleJob(job)}>
                        {job.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button className="admin2-btn-sm admin2-btn-run" onClick={() => handleRunJob(job.name)}>
                        Run Now
                      </button>
                      <button className="admin2-btn-sm" onClick={() => openEditForm(job)}>
                        Edit
                      </button>
                      <button className="admin2-btn-sm admin2-btn-danger" onClick={() => handleDeleteJob(job.name)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}

      {/* Toast */}
      {toast && (
        <div className="admin2-toast-container">
          <div className={`admin2-toast admin2-toast-${toast.type}`}>{toast.message}</div>
        </div>
      )}
    </div>
  );
};

export default CronJobsTab;
